import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import logo from "../../assests/images/pccarelogo.jpg";

const ResetFlow = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const getBaseUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const backendRoot = process.env.REACT_APP_BACKEND;
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    return (backendUrl || apiBase || backendRoot || "http://localhost:8000")
      .trim()
      .replace(/([^:]\/)\/+/g, "$1")
      .replace(/\/+$/, "");
  };

  const extractErrorMessage = (data, fallback) => {
    if (typeof data === "string" && data.trim()) return data;
    if (Array.isArray(data?.message) && data.message.length) {
      return data.message.join(", ");
    }
    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message;
    }
    if (typeof data?.error === "string" && data.error.trim()) {
      return data.error;
    }
    return fallback;
  };

  const buildOtpPayloads = (basePayload, rawOtp, options = {}) => {
    const payloads = [];
    const includeNoOtp = Boolean(options.includeNoOtp);
    const trimmedOtp = String(rawOtp || "").trim();

    if (includeNoOtp) {
      payloads.push({ ...basePayload });
    }

    if (trimmedOtp) {
      payloads.push({ ...basePayload, otp: trimmedOtp });

      const numericOtp = Number(trimmedOtp);
      if (Number.isFinite(numericOtp) && String(numericOtp) === trimmedOtp) {
        payloads.push({ ...basePayload, otp: numericOtp });
      }
    }

    return payloads.length ? payloads : [{ ...basePayload }];
  };

  const postWithPayloadFallback = async (url, payloads, fallbackMessage) => {
    let lastMessage = fallbackMessage;

    for (let index = 0; index < payloads.length; index += 1) {
      const payload = payloads[index];
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.status === 404) {
        throw new Error("Forgot password API is not available on the server.");
      }

      const data = await res.json().catch(() => ({}));
      if (res.ok) return data;

      lastMessage = extractErrorMessage(data, fallbackMessage);
      const retriableStatuses = new Set([400, 401, 403, 422]);
      const canRetry = retriableStatuses.has(res.status) && index < payloads.length - 1;
      if (!canRetry) throw new Error(lastMessage);
    }

    throw new Error(lastMessage);
  };

  React.useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleResendOtp = async () => {
    setError("");
    setSuccess("");

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError("Please enter your registered email.");
      return;
    }

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
    if (!emailOk) {
      setError("Please enter a valid email address.");
      return;
    }

    setResendLoading(true);
    try {
      const baseUrl = getBaseUrl();
      const res = await fetch(`${baseUrl}/customers/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(extractErrorMessage(data, "Failed to resend OTP."));
      }

      if (data?.already_verified) {
        setSuccess(data?.message || "OTP already verified. Please set a new password.");
        setResendCooldown(0);
        return;
      }

      if (data?.otp_sent === false) {
        throw new Error(extractErrorMessage(data, "OTP resend failed."));
      }

      setSuccess(data?.message || "OTP resent successfully.");
      setResendCooldown(30);
    } catch (err) {
      setError(err.message || "Failed to resend OTP.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError("Please enter your registered email.");
      return;
    }

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
    if (!emailOk) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const baseUrl = getBaseUrl();
      await postWithPayloadFallback(
        `${baseUrl}/customers/forgot-password`,
        [{ email: normalizedEmail }],
        "OTP send failed"
      );

      setStep(2);
      setSuccess("OTP sent to your email. Please enter it below.");
    } catch (err) {
      setError(err.message || "OTP send failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const normalizedEmail = email.trim().toLowerCase();
    if (!otp.trim()) {
      setError("Please enter OTP.");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const baseUrl = getBaseUrl();
      await postWithPayloadFallback(
        `${baseUrl}/customers/reset-password`,
        buildOtpPayloads({ email: normalizedEmail, password: newPassword }, otp),
        "Password reset failed"
      );

      setSuccess("Password updated successfully!");
      setTimeout(() => {
        navigate("/login");
      }, 1200);
    } catch (err) {
      setError(err.message || "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f4f6ff] to-[#ffeaea] px-4">
      <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl p-8 transition-all duration-500">
        <div className="flex justify-center mb-2">
          <img src={logo} alt="PC Care" className="h-20 object-contain" />
        </div>

        <h2 className="text-2xl font-bold text-center text-[#1E2A78]">
          {step === 1 ? "Forgot Password" : "Reset Password"}
        </h2>

        <p className="text-sm text-gray-500 text-center mb-6">
          {step === 1
            ? "We will send an OTP to your email"
            : "Enter OTP and set a new password"}
        </p>

        {step === 1 && (
          <form onSubmit={handleSendOTP} className="space-y-4">
            <input
              type="email"
              placeholder="Enter your registered email"
              className="w-full border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-[#1E2A78] outline-none transition"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {error && <p className="text-red-500 text-sm">{error}</p>}
            {success && <p className="text-green-600 text-sm">{success}</p>}

            <button
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#1E2A78] to-[#2E3CB3] text-white p-3 rounded-xl font-semibold shadow-md hover:scale-[1.02] transition disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send OTP"}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <input
              type="text"
              placeholder="Enter OTP"
              className="w-full border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-[#E63946] outline-none transition"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
            />
            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendLoading || resendCooldown > 0}
                className="text-[#1E2A78] font-semibold hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {resendLoading
                  ? "Resending..."
                  : resendCooldown > 0
                    ? `Resend OTP in ${resendCooldown}s`
                    : "Resend OTP"}
              </button>
              <span className="text-gray-400">Didn&apos;t receive OTP?</span>
            </div>

            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="New Password"
                className="w-full border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-[#1E2A78] outline-none transition pr-10"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#1E2A78]"
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                className="w-full border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-[#1E2A78] outline-none transition pr-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#1E2A78]"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}
            {success && <p className="text-green-600 text-sm">{success}</p>}

            <button
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#E63946] to-[#ff5c5c] text-white p-3 rounded-xl font-semibold shadow-md hover:scale-[1.02] transition disabled:opacity-60"
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetFlow;
